import { useCallback, useState } from "react";

const read = (key: string) => {
  try {
    return localStorage.getItem(`tab:${key}`);
  } catch {
    return null;
  }
};

/**
 * A page's sub-tab, remembered: ?tab= in the address wins (links and Back land on it), else the last
 * one used on this device, else the page's default. Picking one keeps it in both.
 */
export function useRememberedTab<T extends string>(key: string, valid: readonly T[], fallback: T): [T, (t: T) => void] {
  const ok = (t: string | null): t is T => !!t && (valid as readonly string[]).includes(t);
  const [tab, set] = useState<T>(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("tab");
    if (ok(fromUrl)) return fromUrl;
    const saved = read(key);
    return ok(saved) ? saved : fallback;
  });
  const choose = useCallback(
    (t: T) => {
      set(t);
      try {
        localStorage.setItem(`tab:${key}`, t);
      } catch {
        // Private browsing: remembered for now only.
      }
      const url = new URL(window.location.href);
      url.searchParams.set("tab", t);
      window.history.replaceState(window.history.state, "", url);
    },
    [key],
  );
  return [tab, choose];
}

/** The last settings section opened (Settings in the sidebar goes back to it). */
export const lastSettings = {
  get: () => read("settings"),
  set: (to: string) => {
    try {
      localStorage.setItem("tab:settings", to);
    } catch {
      // Private browsing.
    }
  },
};
